import { type ReconciliationSummary, reconciliationKeys } from '@taler/api-client'
import type { LocalDate } from '@taler/domain'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { retrying } from '@/app/retrying'
import { useApi } from '@/app/api'
import { isIdempotencyPending, problemDetail, problemProperty } from '../internal/problem.ts'

export interface UseReconciliationsOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}

export interface StartOutcome {
  readonly id: string
  /** True when an open reconciliation already existed and this is it, not a fresh one. */
  readonly resumed: boolean
}

export interface UseReconciliations {
  readonly summaries: ComputedRef<readonly ReconciliationSummary[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly hasMore: ComputedRef<boolean>
  readonly isLoadingMore: ComputedRef<boolean>
  readonly isStarting: ComputedRef<boolean>
  readonly startErrorDetail: ComputedRef<string | undefined>
  readonly startErrorPending: ComputedRef<boolean>
  loadMore(): void
  retry(): void
  /** Creates a new session, or resolves to the one already open for this ledger. */
  start(observedDate: LocalDate): Promise<StartOutcome>
}

export function useReconciliations(options: UseReconciliationsOptions): UseReconciliations {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const listQuery = useInfiniteQuery({
    queryKey: computed(() => reconciliationKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ pageParam, signal }) =>
      api.reconciliations.list(
        options.ledgerId()!,
        pageParam === undefined ? {} : { cursor: pageParam },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: active,
    retry: false,
  })

  const startMutation = useMutation({
    mutationFn: (observedDate: LocalDate) => api.reconciliations.create(options.ledgerId()!, observedDate),
    onSuccess: () =>
      Promise.all(
        reconciliationKeys
          .invalidatedByWrite(options.ledgerId()!)
          .map((key) => queryClient.invalidateQueries({ queryKey: key })),
      ),
  })

  return {
    summaries: computed(() => listQuery.data.value?.pages.flatMap((page) => page.items) ?? []),
    isLoading: computed(() => active.value && listQuery.isPending.value),
    error: computed(() => listQuery.error.value),
    retrying: retrying(listQuery),
    hasMore: computed(() => listQuery.hasNextPage.value),
    isLoadingMore: computed(() => listQuery.isFetchingNextPage.value),
    isStarting: computed(() => startMutation.isPending.value),
    startErrorDetail: computed(() => problemDetail(startMutation.error.value)),
    startErrorPending: computed(() => isIdempotencyPending(startMutation.error.value)),
    loadMore: () => {
      void listQuery.fetchNextPage()
    },
    retry: () => {
      void listQuery.refetch()
    },
    async start(observedDate) {
      try {
        const detail = await startMutation.mutateAsync(observedDate)
        return { id: detail.id, resumed: false }
      } catch (thrown) {
        const openId = problemProperty<string>(thrown as Error, 'id')
        if (openId !== undefined) return { id: openId, resumed: true }
        throw thrown
      }
    },
  }
}
